import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types/product';
import { productService } from '../../services/productService';
import { api } from '../../services/api';
import { showSuccess, showError } from '../../utils/notification';

interface OrderItem {
    productId: number;
    guid?: string;
    productCode?: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
}

interface OrderForm {
    items: OrderItem[];
    deliveryDate: string;
    deliveryAddress: string;
    notes: string;
}

// Ключ для localStorage
const CART_STORAGE_KEY = 'aiser_cart';

// Функция для загрузки корзины из localStorage
const loadCartFromStorage = (): OrderForm => {
    try {
        const saved = localStorage.getItem(CART_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                items: parsed.items || [],
                deliveryDate: parsed.deliveryDate || '',
                deliveryAddress: parsed.deliveryAddress || '',
                notes: parsed.notes || ''
            };
        }
    } catch (error) {
        console.error('Ошибка загрузки корзины из localStorage:', error);
    }
    return {
        items: [],
        deliveryDate: '',
        deliveryAddress: '',
        notes: ''
    };
};

// Функция для сохранения корзины в localStorage
const saveCartToStorage = (cart: OrderForm) => {
    try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
        console.error('Ошибка сохранения корзины в localStorage:', error);
    }
};

// Функция для очистки корзины из localStorage
const clearCartStorage = () => {
    try {
        localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
        console.error('Ошибка очистки корзины из localStorage:', error);
    }
};

export const CreateOrderPage: React.FC = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Инициализируем корзину из localStorage
    const [orderForm, setOrderForm] = useState<OrderForm>(loadCartFromStorage);

    // Загрузка товаров при монтировании компонента
    useEffect(() => {
        loadProducts();
    }, []);

    // Сохранение корзины в localStorage при каждом изменении
    useEffect(() => {
        saveCartToStorage(orderForm);
    }, [orderForm]);

    // Автоматическое добавление товаров из каталога
    useEffect(() => {
        if (products.length > 0) {
            const pendingItems = localStorage.getItem('pendingCartItems');
            if (pendingItems) {
                try {
                    const itemsToAdd: Product[] = JSON.parse(pendingItems);
                    itemsToAdd.forEach(product => {
                        addToOrder(product);
                    });
                    localStorage.removeItem('pendingCartItems');
                } catch (error) {
                    console.error('Ошибка при добавлении товаров из каталога:', error);
                }
            }
        }
    }, [products]);

    const loadProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await productService.getAllProducts();

            if (response.success && response.products) {
                setProducts(response.products);
            } else {
                setError('Не удалось загрузить список товаров');
            }
        } catch (err) {
            console.error('Ошибка загрузки товаров:', err);
            setError('Ошибка загрузки товаров');
        } finally {
            setLoading(false);
        }
    };

    const categories = ['all', ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))];

    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    const addToOrder = (product: Product) => {
        const existingItem = orderForm.items.find(item => item.productId === product.id);

        if (existingItem) {
            // Увеличиваем количество существующего товара
            setOrderForm(prev => ({
                ...prev,
                items: prev.items.map(item =>
                    item.productId === product.id
                        ? {
                            ...item,
                            quantity: Math.min(item.quantity + 1, product.maxQuantity || 999),
                            totalPrice: (Math.min(item.quantity + 1, product.maxQuantity || 999)) * (product.price || 0)
                        }
                        : item
                )
            }));
        } else {
            // Добавляем новый товар
            const newItem: OrderItem = {
                productId: product.id,
                guid: product.guid,
                productCode: product.article,
                productName: product.name,
                quantity: product.minQuantity || 1,
                unitPrice: product.price || 0,
                totalPrice: (product.minQuantity || 1) * (product.price || 0)
            };

            setOrderForm(prev => ({
                ...prev,
                items: [...prev.items, newItem]
            }));
        }
    };

    const updateItemQuantity = (productId: number, quantity: number) => {
        const product = products.find(p => p.id === productId);
        if (!product) return;

        const validQuantity = Math.max(product.minQuantity || 1, Math.min(quantity, product.maxQuantity || 999));

        setOrderForm(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.productId === productId
                    ? {
                        ...item,
                        quantity: validQuantity,
                        totalPrice: validQuantity * item.unitPrice
                    }
                    : item
            )
        }));
    };

    const removeFromOrder = (productId: number) => {
        setOrderForm(prev => ({
            ...prev,
            items: prev.items.filter(item => item.productId !== productId)
        }));
    };

    const getTotalAmount = () => {
        return orderForm.items.reduce((sum, item) => sum + item.totalPrice, 0);
    };

    const getTotalQuantity = () => {
        return orderForm.items.reduce((sum, item) => sum + item.quantity, 0);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'KZT',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getAvailabilityColor = (availability: Product['availability']) => {
        switch (availability) {
            case 'available':
                return 'bg-green-100 text-green-700';
            case 'limited':
                return 'bg-yellow-100 text-yellow-700';
            case 'out_of_stock':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getAvailabilityText = (availability: Product['availability']) => {
        switch (availability) {
            case 'available':
                return 'В наличии';
            case 'limited':
                return 'Ограниченно';
            case 'out_of_stock':
                return 'Нет в наличии';
            default:
                return 'Неизвестно';
        }
    };

    const handleSubmit = async () => {
        try {
            // Подготавливаем данные для API
            const orderData = {
                items: orderForm.items.map(item => ({
                    guid: item.guid,
                    productCode: item.productCode,
                    quantity: item.quantity
                })),
                deliveryAddress: orderForm.deliveryAddress,
                deliveryDate: orderForm.deliveryDate ? `${orderForm.deliveryDate}T10:00:00` : new Date().toISOString(),
                comment: orderForm.notes || ''
            };

            console.log('Отправка заявки в 1С:', orderData);

            // Отправляем заказ через API
            const response = await api.createOrder(orderData);

            if (response.success) {
                console.log('Заказ успешно отправлен в 1С:', response);
                // Очищаем корзину после успешной отправки
                clearCartStorage();
                showSuccess('Заявка успешно отправлена!', 'Ваша заявка отправлена в 1С и будет обработана в ближайшее время');
                navigate('/client/orders');
            } else {
                console.error('Ошибка отправки заказа:', response.message);
                showError('Ошибка отправки заявки', response.message || 'Не удалось отправить заявку');
            }
        } catch (error) {
            console.error('Ошибка при отправке заявки:', error);
            showError('Ошибка', 'Произошла ошибка при отправке заявки. Попробуйте еще раз.');
        }
    };

    const steps = [
        { number: 1, title: 'Выбор продуктов', description: 'Добавьте товары в заявку' },
        { number: 2, title: 'Детали доставки', description: 'Укажите адрес и дату' },
        { number: 3, title: 'Подтверждение', description: 'Проверьте заявку' }
    ];

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Создание заявки</h1>
                    <p className="text-gray-600 mt-1">Создайте новую заявку на покупку продукции</p>
                </div>
                <button
                    onClick={() => navigate('/client/orders')}
                    className="text-gray-600 hover:text-gray-900 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Назад к заявкам
                </button>
            </div>

            {/* Progress Steps */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                    {steps.map((step, index) => (
                        <div key={step.number} className="flex items-center">
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep >= step.number
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-600'
                                }`}>
                                {currentStep > step.number ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    step.number
                                )}
                            </div>
                            <div className="ml-3">
                                <p className={`text-sm font-medium ${currentStep >= step.number ? 'text-blue-600' : 'text-gray-600'
                                    }`}>
                                    {step.title}
                                </p>
                                <p className="text-xs text-gray-500">{step.description}</p>
                            </div>
                            {index < steps.length - 1 && (
                                <div className={`w-24 h-0.5 mx-4 ${currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                                    }`}></div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {currentStep === 1 && (
                        <>
                            {loading ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Загрузка товаров...</p>
                                </div>
                            ) : error ? (
                                <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
                                    <div className="text-red-600 mb-4">{error}</div>
                                    <button
                                        onClick={loadProducts}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                    >
                                        Попробовать снова
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Filters */}
                                    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            {/* Category Filter */}
                                            <div className="flex-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Категория
                                                </label>
                                                <select
                                                    value={selectedCategory}
                                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                >
                                                    <option value="all">Все категории</option>
                                                    {categories.filter(cat => cat !== 'all').map(category => (
                                                        <option key={category} value={category}>{category}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Search */}
                                            <div className="flex-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Поиск
                                                </label>
                                                <div className="relative">
                                                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                    <input
                                                        type="text"
                                                        placeholder="Поиск продуктов..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Products Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {filteredProducts.map((product) => {
                                            const isInCart = orderForm.items.some(item => item.productId === product.id);
                                            return (
                                                <div key={product.id} className={`bg-white rounded-2xl border p-6 transition-all duration-200 ${isInCart
                                                    ? 'border-green-300 bg-green-50 shadow-md'
                                                    : 'border-gray-200 hover:shadow-lg'
                                                    }`}>
                                                    {/* Название товара */}
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                                        {product.name}
                                                    </h3>

                                                    {/* Информация о товаре */}
                                                    <div className="space-y-2 mb-4">
                                                        {product.cod && (
                                                            <div className="flex items-center text-sm">
                                                                <span className="font-medium text-gray-700 mr-2 min-w-[70px]">Код:</span>
                                                                <span className="text-gray-900 font-mono bg-gray-50 px-2 py-0.5 rounded">
                                                                    {product.cod}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {product.article && (
                                                            <div className="flex items-center text-sm">
                                                                <span className="font-medium text-gray-700 mr-2 min-w-[70px]">Артикул:</span>
                                                                <span className="text-gray-900 font-mono bg-gray-50 px-2 py-0.5 rounded">
                                                                    {product.article}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {product.measure && (
                                                            <div className="flex items-center text-sm">
                                                                <span className="font-medium text-gray-700 mr-2 min-w-[70px]">Ед. изм.:</span>
                                                                <span className="text-gray-600">
                                                                    {product.measure}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {product.description && (
                                                            <div className="pt-2 border-t border-gray-100">
                                                                <p className="text-xs text-gray-500 line-clamp-2">
                                                                    {product.description}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {product.availability && (
                                                            <div className="pt-2">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAvailabilityColor(product.availability)}`}>
                                                                    {getAvailabilityText(product.availability)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Кнопка добавления */}
                                                    <button
                                                        onClick={() => addToOrder(product)}
                                                        disabled={product.availability === 'out_of_stock'}
                                                        className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${product.availability === 'out_of_stock'
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : isInCart
                                                                ? 'bg-green-600 text-white hover:bg-green-700'
                                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                                            }`}
                                                    >
                                                        {product.availability === 'out_of_stock' ? (
                                                            <>
                                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                                Нет в наличии
                                                            </>
                                                        ) : isInCart ? (
                                                            <>
                                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                Добавлено в заявку
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                                                </svg>
                                                                Добавить в заявку
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {currentStep === 2 && (
                        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">Детали доставки</h2>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Желаемая дата доставки
                                    </label>
                                    <input
                                        type="date"
                                        value={orderForm.deliveryDate}
                                        onChange={(e) => setOrderForm(prev => ({ ...prev, deliveryDate: e.target.value }))}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Адрес доставки
                                    </label>
                                    <textarea
                                        value={orderForm.deliveryAddress}
                                        onChange={(e) => setOrderForm(prev => ({ ...prev, deliveryAddress: e.target.value }))}
                                        placeholder="Укажите полный адрес доставки..."
                                        rows={3}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Дополнительные комментарии
                                    </label>
                                    <textarea
                                        value={orderForm.notes}
                                        onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Любые дополнительные требования или комментарии..."
                                        rows={4}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">Подтверждение заявки</h2>
                            <div className="space-y-6">
                                {/* Order Summary */}
                                <div>
                                    <h3 className="text-md font-medium text-gray-900 mb-4">Товары в заявке:</h3>
                                    <div className="space-y-3">
                                        {orderForm.items.map((item) => (
                                            <div key={item.productId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                                <div>
                                                    <h4 className="font-medium text-gray-900">{item.productName}</h4>
                                                    <p className="text-sm text-gray-600">{item.quantity} × {formatCurrency(item.unitPrice)}</p>
                                                </div>
                                                <span className="font-semibold text-gray-900">
                                                    {formatCurrency(item.totalPrice)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Delivery Details */}
                                <div>
                                    <h3 className="text-md font-medium text-gray-900 mb-4">Детали доставки:</h3>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                        <div><strong>Дата:</strong> {orderForm.deliveryDate}</div>
                                        <div><strong>Адрес:</strong> {orderForm.deliveryAddress}</div>
                                        {orderForm.notes && (
                                            <div><strong>Комментарии:</strong> {orderForm.notes}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Total */}
                                <div className="border-t pt-4">
                                    <div className="flex items-center justify-between text-xl font-bold">
                                        <span>Общая сумма:</span>
                                        <span className="text-blue-600">{formatCurrency(getTotalAmount())}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Cart Sidebar */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm sticky top-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Корзина</h3>
                            <div className="flex items-center space-x-2">
                                <span className="bg-blue-100 text-blue-600 text-sm font-medium px-2 py-1 rounded-full">
                                    {orderForm.items.length}
                                </span>
                                {orderForm.items.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setOrderForm(prev => ({ ...prev, items: [] }));
                                            clearCartStorage();
                                        }}
                                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Очистить корзину"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {orderForm.items.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                </div>
                                <p className="text-gray-600 text-sm">Корзина пуста</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orderForm.items.map((item) => {
                                    const product = products.find(p => p.id === item.productId);
                                    return (
                                        <div key={item.productId} className="border-b border-gray-100 pb-4">
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="text-sm font-medium text-gray-900 flex-1">
                                                    {item.productName}
                                                </h4>
                                                <button
                                                    onClick={() => removeFromOrder(item.productId)}
                                                    className="text-red-500 hover:text-red-700 ml-2"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => updateItemQuantity(item.productId, item.quantity - 1)}
                                                        disabled={item.quantity <= (product?.minQuantity || 1)}
                                                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={item.quantity}
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value) || 0;
                                                            if (value > 0) {
                                                                updateItemQuantity(item.productId, value);
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            const value = parseInt(e.target.value);
                                                            if (!value || value < (product?.minQuantity || 1)) {
                                                                updateItemQuantity(item.productId, product?.minQuantity || 1);
                                                            }
                                                        }}
                                                        min={product?.minQuantity || 1}
                                                        max={product?.maxQuantity || 999}
                                                        className="w-16 text-center font-medium border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                    <button
                                                        onClick={() => updateItemQuantity(item.productId, item.quantity + 1)}
                                                        disabled={item.quantity >= (product?.maxQuantity || 999)}
                                                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <span className="font-semibold text-blue-600">
                                                    {item.quantity} КГ
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="pt-4 border-t">
                                    <div className="flex items-center justify-between text-lg font-bold">
                                        <span>Итого:</span>
                                        <span className="text-blue-600">{getTotalQuantity()} КГ</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="mt-6 space-y-3">
                            {currentStep < 3 && (
                                <button
                                    onClick={() => setCurrentStep(currentStep + 1)}
                                    disabled={currentStep === 1 && orderForm.items.length === 0}
                                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {currentStep === 1 ? 'Продолжить' : 'Далее'}
                                </button>
                            )}

                            {currentStep === 3 && (
                                <button
                                    onClick={handleSubmit}
                                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                                >
                                    Отправить заявку
                                </button>
                            )}

                            {currentStep > 1 && (
                                <button
                                    onClick={() => setCurrentStep(currentStep - 1)}
                                    className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                                >
                                    Назад
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
